import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { showToast } from '@/services/toast.service';
import TenantConfigService from '@/services/tenantConfig.service';

export default function EllieTab() {
  const queryClient = useQueryClient();
  const [tenantConfigId, setTenantConfigId] = useState('');
  const [allowSqlQueries, setAllowSqlQueries] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: registrationData, isLoading } = useQuery({
    queryKey: ['tenantRegistration'],
    queryFn: async () => {
      return await TenantConfigService.getInstance().getTenantRegistration();
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (registrationData) {
      const { tenant_config } = registrationData;
      setTenantConfigId(tenant_config.id);
      setAllowSqlQueries(tenant_config.ellie_allow_sql_queries ?? false);
      setCustomInstructions(tenant_config.ellie_custom_instructions ?? '');
    }
  }, [registrationData]);

  const save = useCallback(async () => {
    if (!tenantConfigId) return;

    setSaving(true);
    try {
      await TenantConfigService.getInstance().saveEllieSettings(tenantConfigId, {
        ellie_allow_sql_queries: allowSqlQueries,
        ellie_custom_instructions: customInstructions,
      });

      showToast.success('Ellie settings saved');
      queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
    } catch (err: any) {
      showToast.error('Failed to save Ellie settings', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSaving(false);
    }
  }, [tenantConfigId, allowSqlQueries, customInstructions, queryClient]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>SQL Queries</CardTitle>
          <CardDescription>
            Allow Ellie to run arbitrary SQL queries against your data warehouse
            to answer questions. When disabled, Ellie can only use the semantic
            layer (metrics and dimensions).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Allow SQL Queries</Label>
              <p className="text-sm text-muted-foreground">
                {allowSqlQueries
                  ? 'Ellie can run SQL queries against your warehouse'
                  : 'SQL queries are disabled'}
              </p>
            </div>
            <Switch
              checked={allowSqlQueries}
              onCheckedChange={setAllowSqlQueries}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Custom Instructions</CardTitle>
          <CardDescription>
            Add custom instructions that will be appended to Ellie's system
            prompt. Use this to provide business context, define terminology,
            set preferred analysis approaches, or give Ellie any other guidance
            specific to your organization.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            placeholder="e.g. Our fiscal year starts in April. When analyzing revenue, always break down by product line. Use 'customers' instead of 'users' in your responses."
            rows={8}
          />
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving}>
        {saving ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Saving...
          </>
        ) : (
          'Save'
        )}
      </Button>
    </div>
  );
}
